import { DataSourceCategory } from './../../../../../shared/enums/data-source-category';
import { IDialogDataLayers } from '../../../../../shared/interfaces/dialog-data-layers';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ISchedulerSettings } from '../../../../scheduler/interfaces/scheduler-settings.interface';
import { SchedulerSettingsService } from '../../../../scheduler/services/scheduler-settings.service';
import { IProcessSettings } from '../../../../../shared/interfaces/process-settings';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import IZoningCodeRecord = ZoningAPI.IZoningCodeRecord;
import * as _ from 'lodash';
import { appSettings } from 'src/settings/config';

@Component({
    standalone: false,
    selector: 'app-zc-version-calc-process-settings-dialog',
    templateUrl: './app-zc-version-calc-process-settings-dialog.component.html',
    styleUrls: ['./app-zc-version-calc-process-settings-dialog.component.scss']
})
export class ZcVersionCalcProcessSettingsComponent implements OnInit, OnDestroy {
    layerForm: FormGroup;
    isDisabled: boolean;
    processSettings: IProcessSettings = {
        dataSourceCategories: [DataSourceCategory.CITY, DataSourceCategory.COUNTY, DataSourceCategory.STATE],
        maxPRsBunchSize: 1,
        maxThreadsCount: 1,
        useGAPIDataCache: true,
        syncSolrData: true
    };
    schedulerSettings: ISchedulerSettings;
    private destroy$ = new Subject<boolean>();
    showSyncSolrAUDataCheckbox = appSettings.showSyncSolrAUDataCheckbox;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IDialogDataLayers,
        private formBuilder: FormBuilder,
        private readonly schedulerSettingsService: SchedulerSettingsService
    ) {}

    get cachedPRCityCount(): number {
        return _.get(this.data, 'cachedZoningCode.MetaData.cachedPRs.city');
    }

    get cachedPRCountyCount(): number {
        return _.get(this.data, 'cachedZoningCode.MetaData.cachedPRs.county');
    }

    get cachedPRStateCount(): number {
        return _.get(this.data, 'cachedZoningCode.MetaData.cachedPRs.state');
    }

    get projectsDataSourceCategory(): number {
        return _.get(this.data, 'cachedZoningCode.ProjectsDataSourceCategory');
    }

    get isCityDefault(): boolean {
        return this.projectsDataSourceCategory === DataSourceCategory.CITY;
    }

    get isCountyDefault(): boolean {
        return this.projectsDataSourceCategory === DataSourceCategory.COUNTY;
    }

    get isStateDefault(): boolean {
        return this.projectsDataSourceCategory === DataSourceCategory.STATE;
    }

    ngOnInit() {
        this.isDisabled = false;
        this.processSettings.dataSourceCategories = this.data.dataSourceCategories;
        this.processSettings.useGAPIDataCache = this.data.useGAPIDataCache;
        this.processSettings.syncSolrData = this.data.syncSolrData;
        this.layerForm = this.formBuilder.group({
            city: {
                value: this.data.dataSourceCategories.includes(DataSourceCategory.CITY),
                disabled: this.data.isDisabledLayers || this.cachedPRCityCount === 0
            },
            county: {
                value: this.data.dataSourceCategories.includes(DataSourceCategory.COUNTY),
                disabled: this.data.isDisabledLayers || this.cachedPRCountyCount === 0
            },
            state: {
                value: this.data.dataSourceCategories.includes(DataSourceCategory.STATE),
                disabled: this.data.isDisabledLayers || this.cachedPRStateCount === 0
            }
        });
        this.layerForm.valueChanges.subscribe(value => {
            this.processSettings.dataSourceCategories = [];
            for (const layer in value) {
                if (!value[layer]) {
                    continue;
                }
                switch (layer) {
                    case 'city':
                        this.processSettings.dataSourceCategories.push(DataSourceCategory.CITY);
                        break;
                    case 'county':
                        this.processSettings.dataSourceCategories.push(DataSourceCategory.COUNTY);
                        break;
                    case 'state':
                        this.processSettings.dataSourceCategories.push(DataSourceCategory.STATE);
                        break;
                }
            }
            this.isDisabled = !(value.city || value.county || value.state);
        });
        this.schedulerSettingsService.schedulerSettings.pipe(takeUntil(this.destroy$)).subscribe(schedulerSettings => {
            this.schedulerSettings = schedulerSettings;
            this.bunchChanged(
                _.get(this, 'schedulerSettings.defaultMaxPRsBunchSize') || this.processSettings.maxPRsBunchSize
            );
        });
        if (this.data.isDisabledLayers) {
            this.threadsCountChanged(this.data.maxThreadsCount);
            this.bunchChanged(this.data.maxPRsBunchSize);
            this.processSettings.dataSourceCategories = this.data.dataSourceCategories;
        }
    }

    threadsCountChanged(value) {
        this.processSettings.maxThreadsCount = value;
    }

    bunchChanged(value) {
        this.processSettings.maxPRsBunchSize = value;
    }

    useGAPIDataCacheChanged(value: any) {
        this.processSettings.useGAPIDataCache = value.checked;
    }

    syncSolrDataChanged(value: any) {
        this.processSettings.syncSolrData = value.checked;
    }

    isLayerCached(layer: DataSourceCategory) {
        return _.isNumber(layer);
    }

    setCachedZoningCode(zoningCode: IZoningCodeRecord) {
        this.data.cachedZoningCode = zoningCode;
        const controls = this.layerForm.controls;

        controls.city.setValue(controls.city.value || this.isCityDefault);
        controls.county.setValue(controls.county.value || this.isCountyDefault);
        controls.state.setValue(controls.state.value || this.isStateDefault);

        if (this.data.isDisabledLayers || this.cachedPRCityCount === 0) {
            controls.city.setValue(false);
            controls.city.disable();
        }
        if (this.data.isDisabledLayers || this.cachedPRCountyCount === 0) {
            controls.county.setValue(false);
            controls.county.disable();
        }
        if (this.data.isDisabledLayers || this.cachedPRStateCount === 0) {
            controls.state.setValue(false);
            controls.state.disable();
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
